import { Module } from '../../ts/Module';
import { GetExtensionsServerSettingsRequest, IGetExtensionsServerSettingsResult } from '../../ts/ServerApi/requests/settings/extensions/dashboard/GetExtensionsServerSettingsRequest';
import { GetExtsvcServerSettingsRequest, IGetExtsvcServerSettingsResult } from '../../ts/ServerApi/requests/GetExtsvcServerSettingsRequest';
import { GetLastActiveDirectoryGroupsSyncTimeRequest, IGetLastActiveDirectoryGroupsSyncTimeResult } from '../../ts/ServerApi/requests/getLastActiveDirectoryGroupsSyncTimeRequest';
import { GetLastRecommendationTrainTimeRequest, IGetLastRecommendationTrainTimeResult } from '../../ts/ServerApi/requests/GetLastRecommendationTrainTimeRequest';
import { GetServerSettingsRequest, IGetServerSettingsResult } from '../../ts/ServerApi/requests/GetServerSettingsRequest';
import { SetRunNowForSitesRequest } from '../../ts/ServerApi/requests/SetRunNowForSitesRequest';
import { ISetSandboxForAllSitesParams, ISetSandboxForAllSitesResult, SetSandboxSettingsForAllSitesRequest } from '../../ts/ServerApi/requests/SetSandboxSettingsForAllSitesRequest';
import { IUpdateBooleanServerSettingResult, UpdateBooleanServerSettingRequest } from '../../ts/ServerApi/requests/UpdateBooleanServerSettingRequest';
import { IUpdateExtensionsServerSettingsParams, UpdateExtensionsServerSettingsRequest } from '../../ts/ServerApi/requests/settings/extensions/dashboard/UpdateExtensionsServerSettingsRequest';
import { IUpdateExtsvcServerSettingsParams, UpdateExtsvcServerSettingsRequest } from '../../ts/ServerApi/requests/settings/extensions/analytics/UpdateExtsvcServerSettingsRequest';
import { IUpdateScheduleFrequencyServerSettingResult, UpdateScheduleFrequencyServerSettingRequest } from '../../backgroundJob/types/UpdateScheduleFrequencyServerSettingRequest';
import { IUpdateStringServerSettingResult, UpdateStringServerSettingRequest } from '../../ts/ServerApi/requests/UpdateStringServerSettingRequest';
import {
  IEditableServerSettings,
  IDashExtServerSettings,
  IExtsvcServerSettings, IAnalyticsExtensionsServerSettings
} from '../../ts/ServerApi/types/IEditableServerSettings';
import { IResult, ITagResult } from '../../httpClient/serverApi/types/IResponse';
import { IScheduleFrequency } from '../../schedule/types/ISchedule';
import { WebHttpClientWithErrorToast, WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import WebHttpClient from '../../httpClient/WebHttpClient';
import TagHttpClient from '../../httpClient/TagHttpClient';
import {
  GetAnalyticsExtSiteSettingsRequest,
  IGetAnalyticsExtSiteSettingsResult
} from '~src/ts/ServerApi/requests/settings/extensions/analytics/GetAnalyticsExtSiteSettingsRequest';
import {IPrepExtensionsServerSettings} from '~src/settings/types/ISiteSettingsForSiteAdmin';
import {
  IUpdatePrepExtensionsServerSettingsParams,
  UpdatePrepExtensionsServerSettingsRequest
} from '~src/ts/ServerApi/requests/settings/extensions/prep/UpdatePrepExtensionsServerSettingsRequest';


export interface IServerTimestamps {
  lastRecommendationTrainTime: string;
  lastActiveDirectoryGroupsSyncTime: string;
}

export class ServerSettings {

  public fetch(): Promise<IEditableServerSettings> {
    return WebHttpClient.sendRequest(new GetServerSettingsRequest({}))
      .then((result: IGetServerSettingsResult) => result.serverSettings);
  }

  public getExtensionsSettings(): Promise<IDashExtServerSettings> {
    return WebHttpClient.sendRequest(new GetExtensionsServerSettingsRequest({}))
      .then((result: IGetExtensionsServerSettingsResult) => result);
  }

  public getExtsvcSettings(): ng.IPromise<IExtsvcServerSettings> {
    return TagHttpClient.sendTagRequest(new GetExtsvcServerSettingsRequest())
      .then((result: IGetExtsvcServerSettingsResult) => result);
  }

  public getAnalyticsSiteLevel() {
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new GetAnalyticsExtSiteSettingsRequest())
      .then((result: IGetAnalyticsExtSiteSettingsResult) => result);
  }

  public getAnalyticsServerLevel() {
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new GetExtsvcServerSettingsRequest())
      .then((result: IGetExtsvcServerSettingsResult) => result);
  }

  public getTimestamps(): Promise<IServerTimestamps> {
    const timestamps: IServerTimestamps =
      {
        lastRecommendationTrainTime: null,
        lastActiveDirectoryGroupsSyncTime: null
      };
    return WebHttpClient.sendRequest(new GetLastRecommendationTrainTimeRequest())
      .then(
        (result: IGetLastRecommendationTrainTimeResult) => {
          timestamps.lastRecommendationTrainTime = result.time;
          return WebHttpClient.sendRequest(new GetLastActiveDirectoryGroupsSyncTimeRequest())
            .then((result: IGetLastActiveDirectoryGroupsSyncTimeResult) => {
              timestamps.lastActiveDirectoryGroupsSyncTime = result.time;
              return timestamps;
            });
          });
  }

  public updateBoolean(setting: string, value: boolean): Promise<IUpdateBooleanServerSettingResult> {
    const request = new UpdateBooleanServerSettingRequest({
      setting: setting,
      value: value
    });
    return WebHttpClient.sendRequest(request);
  }

  public updateString(setting: string, value: string): Promise<IUpdateStringServerSettingResult> {
    const request = new UpdateStringServerSettingRequest({
      setting: setting,
      value: value
    });
    return WebHttpClient.sendRequest(request);
  }

  public updateScheduleFrequency(setting: string, value: IScheduleFrequency): Promise<IUpdateScheduleFrequencyServerSettingResult> {
    const request = new UpdateScheduleFrequencyServerSettingRequest({
      setting: setting,
      value: value
    });
    return WebHttpClient.sendRequest(request);
  }

  public updateExtensionsSettings(extensionsSettings: IDashExtServerSettings): ng.IPromise<IResult> {
    const params: IUpdateExtensionsServerSettingsParams = {
      settings: extensionsSettings
    };
    return getErrorNotifyingServer().sendRequest(new UpdateExtensionsServerSettingsRequest(params));
  }

  public updatePrepServerExtensionsSettingsForServerAdmin(prepExtensionsSettings: IPrepExtensionsServerSettings): Promise<IResult> {
    const params: IUpdatePrepExtensionsServerSettingsParams = {
      prepExtensionsServerSettings: prepExtensionsSettings
    };
    return getErrorNotifyingServer().sendRequest(new UpdatePrepExtensionsServerSettingsRequest(params));
  }

  public updateExtsvSettings(extsvcSettings: IExtsvcServerSettings): ng.IPromise<IResult> {
    return getErrorNotifyingServer().sendTagRequest(new UpdateExtsvcServerSettingsRequest(extsvcSettings));
  }

  public updateRunNowSettings(sites: string[], shouldEnable: boolean): ng.IPromise<IResult> {
    return getErrorNotifyingServer().sendRequest(new SetRunNowForSitesRequest({sites, shouldEnable}));
  }

  public setSandboxSettingForExistingSites(shouldEnable: boolean): Promise<ISetSandboxForAllSitesResult> {
    const params: ISetSandboxForAllSitesParams = {
      shouldEnable: shouldEnable
    };
    return WebHttpClient.sendRequest(new SetSandboxSettingsForAllSitesRequest(params));
  }

  public updateAnalyticsExtensionsServerSettings(analyticsExtensionsServerSettingsEnabled: IExtsvcServerSettings) {
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new UpdateExtsvcServerSettingsRequest(analyticsExtensionsServerSettingsEnabled));
  }
}

function getSessionManagementService() {
  return getSessionManagementServiceInstance();
}

function getErrorNotifyingServer() {
  return WebHttpClientWithErrorToastInstance;
}

export const ServerSettingsInstance = new ServerSettings();
